import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  createServiceVendor,
  findServiceVendor,
  patchServiceVendor,
} from '../../store/serviceVendor/api';
import {
  reset,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../store/serviceVendor/serviceVendorSlice';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import useToggle from '../../hooks/useToggle';
import { useFormik } from 'formik';
import { omit } from 'lodash';
import serviceVendorSchema from '../../validation/serviceVendorSchema';
import ServiceVendorFormModal from './ServiceVendorFormModal';
import { ServiceVendorExport } from '../../client/services';
import { toast } from 'react-toastify';
import { isServiceVendorReportEnabled } from '../../utils/serviceVendorReportFeature';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const formatMonthValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const currentMonth = () => formatMonthValue(new Date());

const parseMonthValue = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}$/.test(value)) return null;
  const [y, m] = value.split('-').map(Number);
  return new Date(y, m - 1, 1);
};

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.serviceVendor);
  const [search, setSearch] = useState('');
  const { toggle, onOpen, onClose } = useToggle();
  const reportEnabled = isServiceVendorReportEnabled();
  const [reportVendorId, setReportVendorId] = useState('');
  const [reportMonth, setReportMonth] = useState(currentMonth);
  const [exporting, setExporting] = useState(false);

  const vendorOptions = useMemo(
    () =>
      (data || []).map((row) => ({
        id: row.id,
        name: row.name,
      })),
    [data]
  );

  const downloadExport = async (vendorId: string, month: string) => {
    if (!vendorId) {
      toast.error('Select a partner');
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error('Select a valid month');
      return;
    }
    setExporting(true);
    try {
      const result: any = await ServiceVendorExport.find({
        query: { vendorId, month },
      });
      const blob = new Blob([result.csv || ''], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = String(
        result?.vendorName ||
          vendorOptions.find((v) => v.id === vendorId)?.name ||
          vendorId
      )
        .replace(/[^a-zA-Z0-9._-]+/g, '_')
        .slice(0, 60);
      link.download = `Vendor-${safeName}-${month}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(
        `Report downloaded (${result.totalOrders || 0} lines, QAR ${Number(
          result.amountDue || 0
        ).toFixed(2)})`
      );
    } catch (err: any) {
      toast.error(
        err?.message || err?.data?.message || 'Partner report download failed'
      );
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findServiceVendor({
          limit,
          skip,
          ...(search && { search }),
        })
      );
    }
  }, [dispatch, status, search, limit, skip]);

  useEffect(() => {
    dispatch(reset());
  }, [search, dispatch]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      email: '',
      extraMobile1: '',
      extraMobile2: '',
      isActive: true,
    },
    validationSchema: serviceVendorSchema,
    onSubmit: async (values) => {
      const extraMobile2 = String(values.extraMobile2 || '').trim();
      const payload = {
        ...omit(values, ['extraMobile1', 'extraMobile2']),
        mainMobile: String(values.extraMobile1 || '').trim(),
        extraMobiles: extraMobile2 ? [extraMobile2] : [],
      };
      try {
        if (values.id) {
          await dispatch(patchServiceVendor(payload)).unwrap();
          toast.success('Partner updated');
        } else {
          await dispatch(
            createServiceVendor(omit(payload, ['id']) as any)
          ).unwrap();
          toast.success('Partner created');
        }
        onClose();
        formik.resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch {
        /* toast handled */
      }
    },
  });

  return (
    <Page title='Event Service Vendors'>
      <Container maxWidth='xl'>
        <HeaderBreadcrumbs
          heading='Event Service Vendors'
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={onOpen}
            >
              New Vendor
            </Button>
          }
        />

        {reportEnabled && (
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
              Partner monthly report
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <FormControl size='small' sx={{ minWidth: 220 }}>
                <InputLabel id='report-partner'>Partner</InputLabel>
                <Select
                  labelId='report-partner'
                  label='Partner'
                  value={reportVendorId}
                  onChange={(e) => setReportVendorId(String(e.target.value))}
                >
                  <MenuItem value=''>
                    <em>Select partner</em>
                  </MenuItem>
                  {vendorOptions.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  views={['year', 'month']}
                  openTo='month'
                  label='Month'
                  value={parseMonthValue(reportMonth)}
                  onChange={(value: Date | null) => {
                    if (value && !Number.isNaN(value.getTime())) {
                      setReportMonth(formatMonthValue(value));
                    }
                  }}
                  inputFormat='MMMM yyyy'
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      sx={{
                        minWidth: 200,
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: '#EE3750',
                          },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#EE3750',
                        },
                      }}
                    />
                  )}
                  componentsProps={{
                    actionBar: {
                      actions: ['today', 'cancel'],
                    },
                  }}
                />
              </LocalizationProvider>
              <Button
                variant='contained'
                disabled={exporting || !reportVendorId || !reportMonth}
                startIcon={
                  exporting ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : (
                    <Iconify icon='eva:download-fill' />
                  )
                }
                onClick={() => downloadExport(reportVendorId, reportMonth)}
              >
                Download partner report
              </Button>
            </Box>
            {!vendorOptions.length && !isLoading && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                No partners on this page. Search or add a partner first.
              </Typography>
            )}
          </Card>
        )}

        <Card>
          <Searchbar handleSearch={(e: any) => setSearch(e.target.value)} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative', width: '100%' }}>
              <Table size='medium' sx={{ minWidth: 1000, width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Name</TableCell>
                    <TableCell align='center'>Email</TableCell>
                    <TableCell align='center'>Phone</TableCell>
                    <TableCell align='center'>Status</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell colSpan={5} align='center'>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align='center'>{row.name}</TableCell>
                        <TableCell align='center'>{row.email}</TableCell>
                        <TableCell align='center'>{row.mainMobile}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={row.isActive ? 'success' : 'error'}
                          >
                            {row.isActive ? 'Active' : 'Inactive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          {reportEnabled && (
                            <IconButton
                              title='Download monthly report'
                              disabled={exporting}
                              onClick={() =>
                                downloadExport(row.id, reportMonth || currentMonth())
                              }
                            >
                              <Iconify icon='eva:download-fill' />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => {
                              formik.setValues({
                                id: row.id,
                                name: row.name,
                                email: row.email || '',
                                extraMobile1: row.mainMobile || '',
                                extraMobile2: row.extraMobiles?.[0] || '',
                                isActive: row.isActive,
                              });
                              onOpen();
                            }}
                          >
                            <Iconify icon='eva:edit-fill' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(
                Math.max(Math.ceil(totalRecords / limit) - 1, 0),
                currentPage
              )}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
        <ServiceVendorFormModal
          toggle={toggle}
          onClose={() => {
            formik.resetForm();
            onClose();
          }}
          formik={formik}
        />
      </Container>
    </Page>
  );
};

export default Index;
